<template>
  <div>
    <slot name="category" />
    <main v-if="post">
      <div class="content">
        <nav v-if="post.type === 'TYPE_POST'" class="my-breadcrumb" aria-label="breadcrumbs">
          <ul>
            <li>
              <nuxt-link :to="localePath('index')">
                {{ post.storeName.name }}
              </nuxt-link>
            </li>
            <li>
              <i class="core-icons core-icons-chevron-right" /> <nuxt-link :to="localePath('cms-blog')">
                {{ $t('fields.blog') }}
              </nuxt-link>
            </li>
            <li><i class="core-icons core-icons-chevron-right" />{{ post.description.name }}</li>
          </ul>
        </nav>
      </div>

      <div>
        <page-title>{{ post.description.name }}</page-title>
        <article>
          <ContentGenerator :sections="post.sections" />
        </article>
        <div class="content">
          <b-taglist v-if="post.type === 'TYPE_POST'" class="post-tags">
            <b-tag class="author">
              {{ post.creator }} • {{ $moment(post.dateOfPublication ).format('LL') }}
            </b-tag>
            <template v-if="post.tags">
              <b-tag v-for="(tag, index) in post.tags.split(',')" :key="index">
                #{{ tag }}
              </b-tag>
            </template>
          </b-taglist>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import ContentGenerator from '../../components/contentEditorClient/content.vue'
export default {
  name: 'CorePostPage',
  components: { ContentGenerator },
  layout: 'page',
  props: {
    post: {
      type: Object,
      default: () => { }
    },
    slugs: {
      type: Array,
      default: () => []
    }
  },
  head () {
    const i18nHead = this.$nuxtI18nHead({ addSeoAttributes: true })

    const frontBaseUrl = process.env.frontBaseUrl
    const mainLang = process.env.lang
    const actualLanguage = this.$store.state.i18n.locale

    let links = this._clone(i18nHead.link)

    links.forEach((element) => {
      let lang = null
      if (element.hid === 'i18n-alt-' + element.hreflang) {
        lang = element.hreflang.toLowerCase()
      }
      if (element.hid === 'i18n-xd') {
        lang = mainLang
      }
      if (element.hid === 'i18n-can') {
        lang = actualLanguage
      }

      if (lang) {
        const find = this.slugs.find(x => x.lang === lang)
        if (find && find.url) {
          element.href = frontBaseUrl + this.localePath({ name: 'cms-postPage', params: { slug: find.url } }, lang)
          // } else if (this.post.type === 'TYPE_POST') {
        //   element.href = frontBaseUrl + this.localePath('cms-blog', lang)
        } else {
          element.href = frontBaseUrl + this.localePath('index', lang)
          element._remove = true
        }
      }
    })
    const removedLangs = new Set(links.filter(el => el._remove).map(el => (el.hreflang || '').toLowerCase()))
    links = links.filter(element => !element._remove)

    const i18nMeta = i18nHead.meta.filter((m) => {
      if (m.hid && m.hid.startsWith('i18n-og-alt-')) {
        const lang = m.hid.replace('i18n-og-alt-', '').toLowerCase()
        return !removedLangs.has(lang)
      }
      return true
    })
    const canonicalLink = links.find(link => link.hid === 'i18n-can')

    return {
      titleTemplate: '%s',
      title: this.post.description.nameSeo || this.post.description.name,
      meta: [
        { hid: 'description', name: 'description', content: this.post.description.descriptionSeo || this.post.description.description },
        { property: 'og:site_name', content: this.post.storeName ? this.post.storeName.name : '' },
        { property: 'og:type', content: 'article' },
        { property: 'og:title', content: this.post.description.nameSeo || this.post.description.name },
        { property: 'og:description', content: this.post.description.descriptionSeo || this.post.description.description },
        ...(canonicalLink?.href ? [{ property: 'og:url', content: canonicalLink?.href }] : []),

        ...((this.post.mainImage && this.post.mainImage.sizes && this.post.mainImage.sizes.w2000)
          ? [
              { property: 'og:image', content: this.post.mainImage.sizes.w2000 },
              { property: 'og:image:alt', content: this.post.description.name }
            ]
          : []),
        //  Nowe warunek na schuette

        ...(process.env.project === 'schuette'
          ? [{ property: 'article:publisher', content: 'https://www.facebook.com/schuette.shop/' }]
          : []),

        ...(this.post.tags
          ? this.post.tags.split(',').slice(0, 3).map(tag => ({
            property: 'article:tag',
            content: tag.trim()
          }))
          : []),

        { property: 'article:modified_time', content: this.$moment(this.post.dateOfPublication).format('YYYY-MM-DD') },
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: this.post.description.nameSeo || this.post.description.name },
        { name: 'twitter:description', content: this.post.description.descriptionSeo || this.post.description.description },
        { name: 'twitter:url', content: process.env.frontBaseUrl + this.$route.fullPath },
        { name: 'twitter:label1', content: 'Written by' },
        { name: 'twitter:data1', content: this.post.creator },
        { name: 'twitter:label2', content: 'Filed under' },
        { name: 'twitter:data2', content: this.post.tags || '' },
        ...((this.post.twitter_image || (this.post.mainImage && this.post.mainImage.sizes)) ? [{ name: 'twitter:image', content: this.post.mainImage.sizes.w2000 }] : []),
        ...((this.post.og_image || (this.post.mainImage && this.post.mainImage.sizes)) ? [{ property: 'og:image', content: this.post.mainImage.sizes.w2000 }] : []),
        ...(this.post.noIndex ? [{ name: 'robots', content: 'noindex,nofollow' }] : []),
        ...i18nMeta
      ],
      script: [
        { type: 'application/ld+json', json: this.getSchemaOrg },
        { type: 'application/ld+json', json: this.getBreadcrumbSchema }
      ],
      htmlAttrs: {
        ...i18nHead.htmlAttrs
      },
      link: [
        ...this.metaLink(links)
      ]
    }
  },
  computed: {
    getBreadcrumbSchema () {
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: this.post.storeName.name,
            item: process.env.frontBaseUrl + this.localePath('index')
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: this.$t('fields.blog'),
            item: process.env.frontBaseUrl + this.localePath('cms-blog')
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: this.post.description.name,
            item: process.env.frontBaseUrl + this.localePath({ name: 'cms-postPage', params: { slug: this.post.url } })
          }
        ]
      }
      return schema
    },
    getSchemaOrg () {
      const schema = {
        '@context': 'https://schema.org/',
        '@type': 'Article',
        publisher: {
          '@type': 'Organization',
          // name: this.settings.title,
          url: process.env.frontBaseUrl
          // logo: {
          //   '@type': 'ImageObject',
          //   url: this.settings.logo
          // }
        },
        author: {
          '@type': 'Person',
          name: this.post.creator
          // image: {
          //   '@type': 'ImageObject',
          //   url: this.post.primary_author.profile_image,
          //   width: 250,
          //   height: 250
          // },
          // sameAs: []
        },
        headline: this.post.description.nameSeo || this.post.description.name,
        url: process.env.frontBaseUrl + this.$route.fullPath,
        keywords: this.post.tags || '',
        description: this.post.description.descriptionSeo || this.post.description.description,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': process.env.frontBaseUrl
        }

      }
      return schema
    }
  },
  watch: {
    currentLang (lang) {
      const find = this.slugs.find(x => x.lang === lang)
      if (find && find.url) {
        this.$router.push(this.localePath({ name: 'cms-postPage', params: { slug: find.url } }))
      } else if (this.post.type === 'TYPE_POST') {
        this.$router.push(this.localePath('cms-blog'))
      } else {
        this.$router.push(this.localePath('index'))
      }
    }
  }
}
</script>
